/**
 * BackToTop — Custom scroll-to-top button (replaces AntD BackTop)
 *
 * Reusable floating button that appears after scrolling past a threshold.
 * Hides the global AntD BackTop while mounted to avoid duplicates.
 * Fully tokenized via SCSS (.task-back-to-top in _utilities.scss).
 *
 * Usage:
 *   <BackToTop />
 *   <BackToTop visibilityHeight={200} target={() => containerRef.current} />
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { VerticalAlignTopOutlined } from '@ant-design/icons';
import '../../styles/index.scss';

const BackToTop = ({ visibilityHeight, target }) => {
  const [visible, setVisible] = useState(false);

  // Hide the global AntD BackTop while this component is mounted
  useEffect(() => {
    const antBackTop = document.getElementById('backtop');
    if (antBackTop) antBackTop.style.display = 'none';
    return () => {
      if (antBackTop) antBackTop.style.display = '';
    };
  }, []);

  const getScrollTarget = useCallback(() => {
    if (target) return target();
    return window;
  }, [target]);

  const getScrollTop = useCallback(() => {
    const el = getScrollTarget();
    if (el === window) return window.pageYOffset || document.documentElement.scrollTop;
    return el ? el.scrollTop : 0;
  }, [getScrollTarget]);

  useEffect(() => {
    const el = getScrollTarget();
    if (!el) return;

    const handleScroll = () => {
      setVisible(getScrollTop() >= visibilityHeight);
    };

    // Check initial position
    handleScroll();

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [getScrollTarget, getScrollTop, visibilityHeight]);

  const scrollToTop = () => {
    const el = getScrollTarget();
    if (el === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <button
      type="button"
      className={`task-back-to-top ${visible ? 'task-back-to-top--visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Back to top"
    >
      <VerticalAlignTopOutlined />
    </button>
  );
};

BackToTop.propTypes = {
  /** Scroll distance (px) before button appears */
  visibilityHeight: PropTypes.number,
  /** Function returning the scrollable element (defaults to window) */
  target: PropTypes.func,
};

BackToTop.defaultProps = {
  visibilityHeight: 400,
  target: null,
};

export default BackToTop;
